import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { LogOutIcon, UserCogIcon } from '@air/design-icons';
import { Avatar, type AvatarSizes } from '@air/primitive-avatar';
import { DropdownMenuItem, DropdownMenuSeparator } from '@air/primitive-dropdown-menu';
import { formatFullName, formatInitials } from '@air/utilities';

import { Routes } from '~/constants/routes';
import { useAccountContext } from '~/providers/AccountProvider';
import { useLogout } from '~/swr-hooks/auth/useLogout';
import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';

const AVATAR_SIZE: AvatarSizes = 32;

export const MainSideNavItemAccountMenu = memo(() => {
  const { data: account } = useAccountContext();
  const router = useRouter();
  const { logout } = useLogout();

  const handleAccountSettings = useCallback(() => {
    router.push(Routes.account.myAccount);
  }, [router]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  if (!account) {
    return null;
  }

  const label = formatFullName(account.firstName, account.lastName) || account.email;

  return (
    <>
      <div className="flex gap-2 px-1.5 py-1">
        <Avatar
          alt={`${label} avatar`}
          className="shrink-0"
          colorSeed={account.id}
          size={AVATAR_SIZE}
          src={getAvatarImageSrc(account.avatar, AVATAR_SIZE)}
          text={formatInitials(account.firstName, account.lastName)}
        />
        <div className="min-w-0">
          <div className="truncate font-medium ads:text-secondary text-14 text-gray-11">{label}</div>
          <div className="truncate ads:text-metadata text-12 text-gray-9">{account.email}</div>
        </div>
      </div>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-id="ACCOUNT_SETTINGS_LINK"
        label="Account Settings"
        onSelect={handleAccountSettings}
        prefix={<UserCogIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />}
      />
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-id="log-out"
        label="Log Out"
        onSelect={handleLogout}
        prefix={<LogOutIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />}
      />
    </>
  );
});

MainSideNavItemAccountMenu.displayName = 'MainSideNavItemAccountMenu';
